import React, { useState } from 'react';

const OrderDetailsModal = ({ order, isOpen, onClose, onUpdateStatus, isDarkMode }) => {
    const [statusNote, setStatusNote] = useState('');

    if (!isOpen || !order) return null;

    const styles = getStyles(isDarkMode);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const handleAction = (status) => {
        // Can add logic to include statusNote (e.g. rejection reason) later if backend supports it
        onUpdateStatus(order.id, status, statusNote);
        onClose();
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <div>
                        <h2 style={styles.title}>Order #{order.orderNumber}</h2>
                        <span style={styles.date}>{new Date(order.createdAt).toLocaleString()}</span>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>&times;</button>
                </div>

                <div style={styles.body}>
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Customer Details</h4>
                        <div style={styles.infoGrid}>
                            <div style={styles.infoItem}>
                                <label>Name</label>
                                <span>{order.customerName || 'Guest'}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <label>Phone</label>
                                <span>{order.customerPhone || 'N/A'}</span>
                            </div>
                            <div style={styles.infoItem}>
                                <label>Order Type</label>
                                <span>{order.orderType}</span>
                            </div>
                            {order.tableNumber && (
                                <div style={styles.infoItem}>
                                    <label>Table</label>
                                    <span>{order.tableNumber}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Order Items</h4>
                        <div style={styles.itemsList}>
                            {order.items.map((item, idx) => (
                                <div key={idx} style={styles.itemRow}>
                                    <div style={styles.itemQty}>{item.quantity}x</div>
                                    <div style={styles.itemContent}>
                                        <div style={styles.itemName}>{item.menuItemName}</div>
                                        {item.customizations && Object.entries(item.customizations).length > 0 && (
                                            <div style={styles.itemCustom}>
                                                {Object.entries(item.customizations).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                    <div style={styles.itemPrice}>{formatCurrency(item.price * item.quantity)}</div>
                                </div>
                            ))}
                        </div>
                        <div style={styles.totalRow}>
                            <span>Total Amount</span>
                            <span>{formatCurrency(order.totalAmount)}</span>
                        </div>
                    </div>

                    {order.specialInstructions && (
                        <div style={{ ...styles.section, backgroundColor: isDarkMode ? '#333' : '#fff3cd', padding: '10px', borderRadius: '4px' }}>
                            <strong>Note:</strong> {order.specialInstructions}
                        </div>
                    )}

                    <div style={styles.footer}>
                        <div style={styles.statusDisplay}>
                            Current Status: <strong>{order.status}</strong>
                        </div>

                        <div style={styles.actionButtons}>
                            {order.status === 'RECEIVED' && (
                                <>
                                    <button onClick={() => handleAction('REJECTED')} style={{ ...styles.btn, backgroundColor: '#d63031', color: 'white' }}>Reject</button>
                                    <button onClick={() => handleAction('ACCEPTED')} style={{ ...styles.btn, backgroundColor: '#00b894', color: 'white' }}>Accept</button>
                                </>
                            )}
                            {order.status === 'ACCEPTED' && (
                                <button onClick={() => handleAction('PREPARING')} style={{ ...styles.btn, backgroundColor: '#0984e3', color: 'white' }}>Start Preparing</button>
                            )}
                            {order.status === 'PREPARING' && (
                                <button onClick={() => handleAction('READY')} style={{ ...styles.btn, backgroundColor: '#00b894', color: 'white' }}>Mark Ready</button>
                            )}
                            {order.status === 'READY' && (
                                <button onClick={() => handleAction('DISPATCHED')} style={{ ...styles.btn, backgroundColor: '#6c5ce7', color: 'white' }}>Dispatch</button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const getStyles = (isDark) => ({
    overlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modal: {
        backgroundColor: isDark ? '#2d3436' : '#fff',
        width: '90%',
        maxWidth: '600px',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        color: isDark ? '#fff' : '#333',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
    },
    header: {
        padding: '20px',
        borderBottom: isDark ? '1px solid #444' : '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
    },
    title: {
        margin: 0,
        fontSize: '1.5rem'
    },
    date: {
        fontSize: '0.9rem',
        color: isDark ? '#aaa' : '#666'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: '2rem',
        lineHeight: '1',
        cursor: 'pointer',
        color: isDark ? '#aaa' : '#666'
    },
    body: {
        padding: '20px',
        overflowY: 'auto'
    },
    section: {
        marginBottom: '24px'
    },
    sectionTitle: {
        margin: '0 0 12px 0',
        fontSize: '1.1rem',
        borderBottom: `2px solid ${isDark ? '#444' : '#eee'}`,
        paddingBottom: '8px',
        color: isDark ? '#ccc' : '#555'
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
        '& label': {
            fontSize: '0.8rem',
            color: isDark ? '#aaa' : '#888',
            marginBottom: '4px',
            textTransform: 'uppercase'
        },
        '& span': {
            fontWeight: '500'
        }
    },
    itemsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    itemRow: {
        display: 'flex',
        alignItems: 'flex-start',
        borderBottom: isDark ? '1px solid #444' : '1px solid #f9f9f9',
        paddingBottom: '8px'
    },
    itemQty: {
        fontWeight: 'bold',
        marginRight: '12px',
        minWidth: '30px'
    },
    itemContent: {
        flex: 1
    },
    itemName: {
        fontWeight: '500'
    },
    itemCustom: {
        fontSize: '0.85rem',
        color: isDark ? '#bbb' : '#666',
        marginTop: '2px'
    },
    itemPrice: {
        fontWeight: 'bold'
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: isDark ? '1px solid #555' : '1px solid #eee',
        fontSize: '1.2rem',
        fontWeight: 'bold'
    },
    footer: {
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '20px',
        borderTop: isDark ? '1px solid #444' : '1px solid #eee'
    },
    actionButtons: {
        display: 'flex',
        gap: '10px'
    },
    btn: {
        padding: '10px 20px',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: '500'
    }
});

export default OrderDetailsModal;
